import { useState, useEffect, useRef } from 'react';
import type { Program, ProgramCategory } from '@/mocks/adminPrograms';
import { categoryLabels } from '@/mocks/adminPrograms';
import { SwalAlert } from '@/hooks/useSweetAlert';
import AdminImageUpload from '@/components/base/AdminImageUpload';

export interface ProgramFormData {
  name: string;
  hostName: string;
  coHosts: string;
  category: ProgramCategory;
  description: string;
  imageUrl: string;
  schedule: string;
  days: string;
}

interface ProgramFormModalProps {
  open: boolean;
  program: Program | null;
  onClose: () => void;
  onSave: (data: ProgramFormData) => void;
}

const defaultForm: ProgramFormData = {
  name: '',
  hostName: '',
  coHosts: '',
  category: 'musical',
  description: '',
  imageUrl: '',
  schedule: '',
  days: 'Lunes, Martes, Miércoles, Jueves, Viernes',
};

const allCategories: ProgramCategory[] = ['musical', 'noticias', 'deportes', 'entretenimiento', 'entrevistas', 'cultural', 'tecnologia', 'variedades'];

const daySuggestions = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function ProgramFormModal({ open, program, onClose, onSave }: ProgramFormModalProps) {
  const [form, setForm] = useState<ProgramFormData>(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const imageBlobRef = useRef<string | null>(null);

  useEffect(() => {
    if (program) {
      setForm({
        name: program.name,
        hostName: program.hostName,
        coHosts: program.coHosts.join(', '),
        category: program.category,
        description: program.description,
        imageUrl: program.imageUrl,
        schedule: program.schedule,
        days: program.days.join(', '),
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
  }, [program, open]);

  const update = (field: keyof ProgramFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleImageChange = (file: File) => {
    if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
    const blobUrl = URL.createObjectURL(file);
    imageBlobRef.current = blobUrl;
    update('imageUrl', blobUrl);
    SwalAlert.toast('Imagen cargada correctamente.', 'success');
  };

  const handleImageRemove = () => {
    if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
    imageBlobRef.current = null;
    update('imageUrl', '');
  };

  const toggleDay = (day: string) => {
    const current = form.days.split(',').map((d) => d.trim()).filter(Boolean);
    const exists = current.includes(day);
    const updated = exists ? current.filter((d) => d !== day) : [...current, day];
    update('days', updated.join(', '));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'El nombre es obligatorio';
    if (!form.hostName.trim()) errs.hostName = 'El locutor es obligatorio';
    if (!form.description.trim()) errs.description = 'La descripción es obligatoria';
    if (!form.schedule.trim()) errs.schedule = 'El horario es obligatorio';
    if (!form.days.trim()) errs.days = 'Seleccioná al menos un día';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    onSave(form);
    setSaving(false);
    await SwalAlert.toast(program ? 'Programa actualizado.' : 'Programa creado.', 'success');
  };

  if (!open) return null;

  const selectedDays = form.days.split(',').map((d) => d.trim()).filter(Boolean);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-12 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg overflow-hidden shadow-xl animate-fade-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div>
            <h3 className="text-base font-bold text-foreground-950">
              {program ? 'Editar Programa' : 'Crear Programa'}
            </h3>
            <p className="text-xs text-foreground-400 mt-0.5">
              {program ? 'Modificá los datos del programa.' : 'Completá los datos del nuevo programa.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200 text-foreground-400 hover:text-foreground-700 cursor-pointer transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Name */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Nombre del programa <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all ${
                errors.name ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
              }`}
              placeholder="Ej. El Mañanero"
            />
            {errors.name && <p className="text-[10px] text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Host + Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
                Locutor principal <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.hostName}
                onChange={(e) => update('hostName', e.target.value)}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all ${
                  errors.hostName ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
                }`}
                placeholder="Ej. Valentina Ríos"
              />
              {errors.hostName && <p className="text-[10px] text-red-500 mt-1">{errors.hostName}</p>}
            </div>
            <div>
              <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
                Categoría <span className="text-red-500">*</span>
              </label>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value as ProgramCategory)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all cursor-pointer"
              >
                {allCategories.map((c) => (
                  <option key={c} value={c}>{categoryLabels[c]}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Co-hosts */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Co-locutores
            </label>
            <input
              type="text"
              value={form.coHosts}
              onChange={(e) => update('coHosts', e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
              placeholder="Separar con comas, ej: Carlos Mendoza, María José Herrera"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Descripción <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              rows={3}
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none ${
                errors.description ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
              }`}
              placeholder="Describí el programa..."
              maxLength={500}
            />
            {errors.description && <p className="text-[10px] text-red-500 mt-1">{errors.description}</p>}
            <p className="text-[10px] text-foreground-400 mt-1 text-right">{form.description.length}/500</p>
          </div>

          {/* Schedule + Days */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
                Horario <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.schedule}
                onChange={(e) => update('schedule', e.target.value)}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all ${
                  errors.schedule ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
                }`}
                placeholder="Ej. 06:00 - 10:00"
              />
              {errors.schedule && <p className="text-[10px] text-red-500 mt-1">{errors.schedule}</p>}
            </div>
            <div>
              <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
                Días
              </label>
              <div className="flex items-center gap-1 flex-wrap">
                {daySuggestions.map((day) => {
                  const active = selectedDays.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
                        active
                          ? 'bg-primary-500 text-background-50 border-primary-500'
                          : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                      }`}
                    >
                      {day.slice(0, 3)}
                    </button>
                  );
                })}
              </div>
              {errors.days && <p className="text-[10px] text-red-500 mt-1">{errors.days}</p>}
            </div>
          </div>

          {/* Image */}
          <div>
            <AdminImageUpload
              label="Imagen del programa"
              currentImage={form.imageUrl}
              onChange={handleImageChange}
              onRemove={handleImageRemove}
              recommendedDimensions="800×450px"
              previewHeight="h-40"
              placeholderIcon="ri-image-add-line"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2.5 px-6 py-4 border-t border-background-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </span>
            ) : (
              program ? 'Guardar Cambios' : 'Crear Programa'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}